import React from 'react';

/**
 * Top-level Error Boundary — catches uncaught render errors and offers the
 * user a way to report them. On catch, dispatches an `open-bug-report` window
 * event with the error pre-filled; BugReportRoot picks it up and opens the
 * modal. If the bug UI isn't mounted (e.g. user signed out), we still show
 * a minimal fallback so the app doesn't display a blank white screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Best-effort: log to console (consoleBuffer will pick it up too)
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo?.componentStack);

    // Notify the bug-report UI if it's mounted. It will open the modal with
    // the error pre-filled. If not mounted (boundary fires before mount, or
    // on a public page), the event is a no-op.
    try {
      window.dispatchEvent(
        new CustomEvent('open-bug-report', {
          detail: {
            prefill: {
              description: `[Erreur automatique] ${String(error?.message || error)}`,
              errorStack: [
                String(error?.stack || error || ''),
                errorInfo?.componentStack
                  ? `\n\nComponent stack:\n${errorInfo.componentStack}`
                  : '',
              ].join(''),
              severity: 'high',
            },
          },
        }),
      );
    } catch { /* never throw from a boundary */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleReport = () => {
    try {
      window.dispatchEvent(
        new CustomEvent('open-bug-report', {
          detail: {
            prefill: {
              description: `[Erreur automatique] ${String(this.state.error?.message || this.state.error)}`,
              errorStack: String(this.state.error?.stack || ''),
              severity: 'high',
            },
          },
        }),
      );
    } catch { /* noop */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <div className="text-2xl mb-2">⚠️</div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            Une erreur est survenue
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            L'application a rencontré un problème inattendu. Tu peux nous le
            signaler pour qu'on le corrige rapidement, ou recharger la page.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleReport}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
            >
              Signaler le bug
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              Recharger
            </button>
          </div>
          {this.state.error?.message && (
            <details className="mt-6 text-xs text-slate-500">
              <summary className="cursor-pointer">Détails techniques</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all bg-slate-50 p-3 rounded">
                {String(this.state.error.message)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
